import { Grid } from '@material-ui/core';
import { Button, Dialog, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { useOnlineStatus } from '../../utilities';
import RenderTreeView from '../AssetLocationTreeView';
import useStyles from './ElectricalAssetLocationDetails.styles';
dayjs.extend(utc);
import { areaDetailsProperties } from '../../graphql';
import type { AssetsByAreaHeirachy } from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import { MobileComponentLocationHeirarchy } from '../../rxdb/inspectionList/queryBuilder';
import { isJestTest } from '../../test-helpers/isJestTest';
import {
  mockChildAreaDetails,
  mockParentAreaDetails,
} from '../../test-helpers/mockData/assetDetails';
import { SiteLayerId } from '../../utilities/enums';
import { downloadFile, setValueinLocalStorage } from '../../utilities/utilityFunctions';
import { FileArea, SuperArea } from '../model/model';

interface Props {
  assetLocationDialogTree: boolean;
  updateAssetLocationDialogStatus: (IDstatus: boolean) => void;
  assetButton: string;
  assetLocationName: string;
  assetName: string;
  assetsByAreaHierarchyData: AssetsByAreaHeirachy[];
  areaListFromRxDB: MobileComponentLocationHeirarchy[];
}

const ElectricalAssetLocationDetails = ({
  assetLocationDialogTree,
  assetLocationName,
  updateAssetLocationDialogStatus,
  assetButton,
  assetName,
  assetsByAreaHierarchyData,
  areaListFromRxDB,
}: Props) => {
  const classes = useStyles();
  const online = useOnlineStatus();
  const [areaProperties, setareaProperties] = useState<SuperArea>({} as SuperArea);
  const [parentName, setParentName] = useState('');
  const [dynamicSelectedLocation, setDynamicSelectedLocation] = useState<number>();
  const offlineTextMsg = 'User Need To Connected With Internet To See This Data.';

  const fetchAllData = async () => {
    if (dynamicSelectedLocation) {
      const result = isJestTest()
        ? mockChildAreaDetails
        : await areaDetailsProperties(dynamicSelectedLocation);
      const allData = result?.data?.areaDetails;
      if (allData?.area.fkParentArea) {
        const parentAreaLocation = isJestTest()
          ? mockParentAreaDetails
          : await areaDetailsProperties(allData?.area.fkParentArea);
        setParentName(parentAreaLocation.data?.areaDetails.area.name);
      } else {
        if (allData?.area.siteLayer === SiteLayerId.Surface) setParentName('Surface');
        if (allData?.area.siteLayer === SiteLayerId.Shaft) setParentName('Shaft');
        else setParentName('Underground');
      }
      setareaProperties(allData);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [dynamicSelectedLocation]);

  const updateOnChange = async (val: number) => {
    setDynamicSelectedLocation(val);
    setValueinLocalStorage('id', val.toString());
  };

  return (
    <div className={classes.scrollHide}>
      <Dialog
        open={assetLocationDialogTree}
        titleText="Asset Location Details"
        onClose={() => updateAssetLocationDialogStatus(false)}
        backButton={false}
        dialogActions={
          <Grid container justifyContent="flex-end" className={classes.dialogActionGrid}>
            <Button
              onClick={() => updateAssetLocationDialogStatus(false)}
              variant="contained"
              color="primary"
              className={classes.dialogBtn}
              noMinHeight
              data-testid="close-treeview-dialog"
            >
              Close
            </Button>
          </Grid>
        }
        fullScreen
        maxWidth="xs"
        noMinHeight
      >
        <Grid className={classes.gridFlex}>
          <Grid item container xs={6} className={`${classes?.root} ${classes.treeViewGrid}`}>
            <RenderTreeView
              onChange={updateOnChange}
              assetButton={assetButton}
              assetLocationName={assetLocationName}
              assetName={assetName}
              assetsByAreaHierarchyData={assetsByAreaHierarchyData}
              areaListFromRxDB={areaListFromRxDB}
            />
          </Grid>

          {online ? (
            <Grid item container xs={6} className={classes.root}>
              <Grid container xs={12} className={classes.areaPropertiesMainGrid}>
                <Grid container xs={6} className={classes.areaPropertiesChildGrid}>
                  <Typography variant="h5" className={classes.areaPropertiesText}>
                    Area Properties
                  </Typography>
                  <Grid xs={12} className={classes.areaPropertiesSubChildGrid}>
                    <Grid
                      item
                      container
                      xs={12}
                      className={`${classes.assetProperties} ${classes.parentAreaGrid}`}
                    >
                      <Grid item xs={12} className={classes.gridFlex}>
                        <Grid item xs={5}>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            fontWeight="normal"
                            className={classes.textlabel}
                          >
                            {`Parent Area`}
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="subtitle1" fontWeight="normal">
                            {parentName ? parentName : '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} className={classes.assetProperties}>
                      <Grid item xs={12} className={classes.gridFlex}>
                        <Grid item xs={5}>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            fontWeight="normal"
                            className={classes.textlabel}
                          >
                            Area Type
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="subtitle1" fontWeight="normal">
                            Room
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} className={classes.assetProperties}>
                      <Grid item xs={12} className={classes.gridFlex}>
                        <Grid item xs={5}>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            fontWeight="normal"
                            className={classes.textlabel}
                          >
                            Room Name
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="subtitle1" fontWeight="normal">
                            {areaProperties ? areaProperties?.area?.name : '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} className={classes.assetProperties}>
                      <Grid item xs={12} className={classes.gridFlex}>
                        <Grid item xs={5}>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            fontWeight="normal"
                            className={classes.textlabel}
                          >
                            Room ID
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="subtitle1" fontWeight="normal">
                            {areaProperties ? areaProperties?.area?.pcsId : '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} className={classes.assetProperties}>
                      <Grid item xs={12} className={classes.gridFlex}>
                        <Grid item xs={5}>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            fontWeight="normal"
                            className={classes.textlabel}
                          >
                            Room Layout
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          {areaProperties?.area?.fileArea?.length
                            ? areaProperties?.area?.fileArea?.map((file: FileArea) => {
                                return (
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="normal"
                                    onClick={() => downloadFile(file.fkFileAims)}
                                  >
                                    {file.name}
                                  </Typography>
                                );
                              })
                            : '-'}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} className={classes.assetProperties}>
                      <Grid item xs={12} className={classes.gridFlex}>
                        <Grid item xs={5}>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            fontWeight="normal"
                            className={classes.textlabel}
                          >
                            Comments
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="subtitle1" fontWeight="normal">
                            {areaProperties?.area?.comment ? areaProperties?.area?.comment : '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid xs={12} className={classes.areaPropertiesSubChildGrid}>
                  <Typography variant="h5" className={classes.riskAssementText}>
                    Risk Assessment
                  </Typography>
                  <Grid
                    item
                    container
                    xs={12}
                    className={`${classes.assetProperties} ${classes.parentAreaGrid}`}
                  >
                    <Grid item xs={12} className={classes.gridFlex}>
                      <Grid item xs={5}>
                        <Typography
                          variant="subtitle1"
                          color="textSecondary"
                          fontWeight="normal"
                          className={classes.textlabel}
                        >
                          Category
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="subtitle1" fontWeight="normal">
                          {areaProperties?.electHistoryAreaRiskResult &&
                          areaProperties?.electHistoryAreaRiskResult[0]?.category
                            ? areaProperties?.electHistoryAreaRiskResult[0]?.category
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12} className={classes.assetProperties}>
                    <Grid item xs={12} className={classes.gridFlex}>
                      <Grid item xs={5}>
                        <Typography
                          variant="subtitle1"
                          color="textSecondary"
                          fontWeight="normal"
                          className={classes.textlabel}
                        >
                          Assessed Value
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="subtitle1" fontWeight="normal">
                          {areaProperties?.electHistoryAreaRiskResult &&
                          areaProperties?.electHistoryAreaRiskResult[0]?.riskRankValue &&
                          areaProperties?.electHistoryAreaRiskResult[0]?.riskRankName
                            ? `${areaProperties?.electHistoryAreaRiskResult[0]?.riskRankValue} - ${areaProperties?.electHistoryAreaRiskResult[0]?.riskRankName}`
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12} className={classes.assetProperties}>
                    <Grid item xs={12} className={classes.gridFlex}>
                      <Grid item xs={5}>
                        <Typography
                          variant="subtitle1"
                          color="textSecondary"
                          fontWeight="normal"
                          className={classes.textlabel}
                        >
                          Comments
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="subtitle1" fontWeight="normal">
                          {areaProperties?.electHistoryAreaRiskResult &&
                          areaProperties?.electHistoryAreaRiskResult[0]?.comment
                            ? areaProperties?.electHistoryAreaRiskResult[0]?.comment
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid
              item
              container
              xs={6}
              alignContent="center"
              justifyContent="center"
              className={`${classes.root} ${classes.offlineText}`}
            >
              {offlineTextMsg}
            </Grid>
          )}
        </Grid>
      </Dialog>
    </div>
  );
};

export default observer(ElectricalAssetLocationDetails);
